/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-24
 */

import { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Button,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  MoreVert,
  Person,
  Email,
  Schedule,
  Message,
} from '@mui/icons-material';

// Add local dependencies.
import {
  JoinRequestCardProps,
  JoinRequestStatus,
} from '../../../types/joinRequest';
import { StudioRole } from '../../../types/studio';
import { getRoleDisplayName, getRoleColor } from '../../../utils/permissions';
import RequestStatusBadge from './RequestStatusBadge';

/**
 * JoinRequestCard component displays an individual join request
 * with user information, request details, and action buttons.
 */
const JoinRequestCard = ({
  request,
  onApprove,
  onDeny,
  onView,
  showActions = true,
}: JoinRequestCardProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleApprove = () => {
    const requestedRole =
      request.requestedRole || request.requested_role || StudioRole.MEMBER;
    onApprove?.(request.id, requestedRole);
    handleMenuClose();
  };

  const handleDeny = () => {
    onDeny?.(request.id, 'Request denied by administrator');
    handleMenuClose();
  };

  const handleView = () => {
    onView?.(request.id);
    handleMenuClose();
  };

  const isPending = request.status === JoinRequestStatus.PENDING;
  const canTakeAction = showActions && isPending;
  // The backend sends no user when the request has no approvable account
  const canApprove = Boolean(request.user);

  return (
    <Card
      sx={{
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 2,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <CardContent>
        {/* Header with User Info and Status */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={request.user?.avatar || undefined}
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'primary.main',
              }}
            >
              <Person />
            </Avatar>
            <Box>
              <Typography variant="h6" component="div">
                {request.user?.firstName && request.user?.lastName
                  ? `${request.user.firstName} ${request.user.lastName}`
                  : request.full_name ||
                    `${request.first_name} ${request.last_name}`}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.5,
                }}
              >
                <Email fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {request.user?.email || request.email}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RequestStatusBadge
              status={request.status}
              submittedAt={request.submitted_at}
              reviewedAt={request.reviewed_at}
              size="small"
            />
            {showActions && (
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                aria-label="More actions"
              >
                <MoreVert />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Request Details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Requested Role */}
          {(request.requestedRole || request.requested_role) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Requested Role:
              </Typography>
              <Chip
                label={getRoleDisplayName(
                  (request.requestedRole || request.requested_role)!
                )}
                size="small"
                sx={{
                  bgcolor: getRoleColor(
                    (request.requestedRole || request.requested_role)!
                  ),
                  color: 'white',
                  fontWeight: 'medium',
                }}
              />
            </Box>
          )}

          {/* Request Message */}
          <Box>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
            >
              <Message fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Message:
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                pl: 2.5,
                fontStyle: request.message ? 'normal' : 'italic',
                color: request.message ? 'text.primary' : 'text.secondary',
              }}
            >
              {request.message || 'No message provided'}
            </Typography>
          </Box>

          {/* Timestamps */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Schedule fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              Submitted: {new Date(request.submitted_at).toLocaleDateString()}{' '}
              at {new Date(request.submitted_at).toLocaleTimeString()}
            </Typography>
          </Box>

          {/* Review Information */}
          {request.reviewed_at && (
            <Typography variant="caption" color="text.secondary">
              Reviewed: {new Date(request.reviewed_at).toLocaleDateString()} at{' '}
              {new Date(request.reviewed_at).toLocaleTimeString()}
              {request.reviewed_by_name && ` by ${request.reviewed_by_name}`}
            </Typography>
          )}

          {/* Requests the backend can't approve */}
          {isPending && !canApprove && (
            <Typography variant="caption" color="text.secondary">
              This request isn&apos;t linked to a verified account, so it
              can&apos;t be approved.
            </Typography>
          )}

          {/* Denial Reason */}
          {request.status === JoinRequestStatus.REJECTED &&
            request.denialReason && (
              <Box sx={{ mt: 1, p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
                <Typography
                  variant="caption"
                  color="error.main"
                  fontWeight="medium"
                >
                  Denial Reason:
                </Typography>
                <Typography variant="body2" color="error.main">
                  {request.denialReason}
                </Typography>
              </Box>
            )}
        </Box>
      </CardContent>

      {/* Quick Actions for Pending Requests */}
      {canTakeAction && (
        <>
          <Divider />
          <CardActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
            <Button
              size="small"
              color="error"
              startIcon={<Cancel />}
              onClick={handleDeny}
            >
              Deny
            </Button>
            {canApprove && (
              <Button
                size="small"
                color="success"
                variant="contained"
                startIcon={<CheckCircle />}
                onClick={handleApprove}
              >
                Approve
              </Button>
            )}
          </CardActions>
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleView}>View Details</MenuItem>
        {isPending && canApprove && (
          <MenuItem onClick={handleApprove}>
            <CheckCircle fontSize="small" sx={{ mr: 1 }} />
            Approve Request
          </MenuItem>
        )}
        {isPending && (
          <MenuItem onClick={handleDeny}>
            <Cancel fontSize="small" sx={{ mr: 1 }} />
            Deny Request
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};

export default JoinRequestCard;
